import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import { GEOMETRY_TYPE, COLOR } from '@tmapy/config';
import { createSegments, distanceBetweenPoints, getValueWithUnits, roundNumberByUnitsLength, calculateArea, } from '../measure';
import { getCommonRotateData, adjustRotateData, offsetCoordinate } from './rotateText';
import { getText, getTextStyle } from './text';
/**
 * Style function for draw tool(ol.interaction.draw)
 * styles with showing texts for objects
 */
export function getDrawStyles(feature, resolution, projection, style, drawType) {
    let featureStyles = [];
    const geometry = feature.getGeometry();
    const geomType = geometry.getType();
    // get DrawType from feature.geometry??!
    if (drawType === GEOMETRY_TYPE.POLYGON && geomType === GEOMETRY_TYPE.POLYGON) {
        const measurements = calculatePolygonMeasurements(geometry, projection, false);
        featureStyles.push(...getPolygonStyles(geometry, style, {
            geomType,
            projection,
            resolution,
            measurements,
        }));
    }
    if (drawType === GEOMETRY_TYPE.LINE_STRING && geomType === GEOMETRY_TYPE.LINE_STRING) {
        const segments = calculateLineMeasurements(geometry, projection);
        featureStyles.push(...getLineStyles(geometry, style, { segments, resolution }));
    }
    if (geomType === GEOMETRY_TYPE.CIRCLE) {
        const circle = geometry;
        const cursorLine = new LineString([circle.getCenter(), circle.getLastCoordinate()]);
        const lineStyle = getHelperLineStyle(style.color);
        lineStyle[0].setGeometry(cursorLine);
        lineStyle[1].setGeometry(cursorLine);
        featureStyles = [
            ...featureStyles,
            ...getDrawCircleTextStyle(circle, resolution, style, cursorLine),
            ...lineStyle,
        ];
    }
    return featureStyles;
}
/**
 * Function return styles fro active objects(line, polygon)
 *
 */
export function getStyles(feature, resolution, projection, style) {
    const geometry = feature.getGeometry();
    const geomType = geometry.getType();
    const isCircle = !!feature.get('isCircle');
    let measurements;
    if (geomType === GEOMETRY_TYPE.POLYGON) {
        measurements = calculatePolygonMeasurements(geometry, projection, isCircle);
    }
    if (geomType === GEOMETRY_TYPE.POLYGON || geomType === GEOMETRY_TYPE.CIRCLE) {
        return getPolygonStyles(geometry, style, {
            geomType,
            projection,
            resolution,
            isCircle,
            measurements,
        });
    }
    if (geomType === GEOMETRY_TYPE.LINE_STRING) {
        const segments = calculateLineMeasurements(geometry, projection);
        return getLineStyles(geometry, style, { segments, resolution });
    }
    return [];
}
export function getPolygonStyles(geometry, style, { geomType, projection, resolution, isCircle, measurements, }) {
    let textStyles = [];
    if (geomType === GEOMETRY_TYPE.POLYGON) {
        textStyles = [
            ...textStyles,
            ...getPolygonTextStyles(geometry, resolution, measurements.mapSegments, measurements.totalArea, style, isCircle),
        ];
    }
    if (geomType === GEOMETRY_TYPE.CIRCLE || isCircle) {
        textStyles = [
            ...textStyles,
            ...getCircleTextStyle(geometry, resolution, projection, style),
        ];
    }
    return textStyles;
}
export function calculateLineMeasurements(geometry, projection) {
    const coordinates = geometry.getCoordinates();
    return createSegments(coordinates, projection);
}
export function getLineStyles(geometry, style, { segments, resolution }) {
    let textStyles = [];
    const coordinates = geometry.getCoordinates();
    let textStyleSum = [];
    // sum of lengths
    if (coordinates.length > 2) {
        const lastSegment = segments[segments.length - 1];
        const sumText = `${lastSegment.cumulativeSumFormat} ${lastSegment.cumulativeSumUnits}`;
        const sumTextData = getLineSumText(coordinates, resolution);
        textStyleSum = getTextStyle({
            text: sumText,
            geometry: sumTextData.point,
            rotation: sumTextData.rotation,
            color: style.color,
            overflow: false,
            textAlign: sumTextData.textAlign,
        });
    }
    textStyles = [
        ...textStyleSum,
        ...textStyles,
        ...getSegmentsStyles(coordinates, segments, resolution, style),
    ];
    return textStyles;
}
export function getTextData(coordinates, index, segments, resolution) {
    let text = '';
    let rotationData = {
        rotation: 0,
        point: null,
    };
    if (index > 0 && coordinates.length > 1) {
        text = `${segments[index].valueFormat} ${segments[index].units}`;
        rotationData = adjustRotateData(segments[index].rotation, segments[index].point, resolution);
    }
    return {
        text,
        textAlign: 'center',
        point: rotationData.point,
        rotation: rotationData.rotation,
    };
}
export function getSegmentsStyles(coordinates, segments, resolution, style, counter = 1) {
    let textData, textStyleSegment = [];
    const segmentsStyles = [];
    for (let index = counter; index < coordinates.length; index++) {
        textData = getTextData(coordinates, index, segments, resolution);
        if (textData.text) {
            const line = new LineString([coordinates[index - 1], coordinates[index]]);
            textStyleSegment = getTextStyle({
                text: textData.text,
                color: style.color,
                geometry: line,
                rotation: textData.rotation,
                textAlign: textData.textAlign,
                offsetY: textData.rotation < Math.PI && textData.rotation > -Math.PI ? -10 : 10,
                placement: 'line',
                overflow: false,
            });
            segmentsStyles.push(...textStyleSegment);
        }
    }
    return segmentsStyles;
}
export function getCircleTextStyle(geometry, resolution, projection, style) {
    // TODO pro vypocet do props
    const center = geometry.getType() === GEOMETRY_TYPE.POLYGON
        ? geometry.getInteriorPoint().getCoordinates()
        : geometry.getCenter();
    const end = geometry.getLastCoordinate();
    const radiusData = distanceBetweenPoints(center, end, projection);
    const { rotation, coordinate } = getCommonRotateData(center, end);
    // TODO pro generovani stylu
    const rotationData = adjustRotateData(rotation, coordinate, resolution);
    const lineStyles = getHelperLineStyle(style.color);
    const line = new LineString([center, end]);
    lineStyles[0].setGeometry(line);
    lineStyles[0].setText(getText({
        text: `${radiusData.value} ${radiusData.units}`,
        color: style.color,
        rotation: rotationData.rotation,
        offsetY: -10,
        textAlign: 'center',
        placement: 'line',
        overflow: false,
    }));
    lineStyles[1].setGeometry(line);
    return lineStyles;
}
export function getDrawCircleTextStyle(geometry, resolution, style, cursorLine) {
    let textStyleRadius = [];
    if (cursorLine) {
        // TODO cast pro vypocet do props
        let radius = geometry.getRadius();
        const convertedRadius = getValueWithUnits(radius);
        radius = roundNumberByUnitsLength(convertedRadius.units, convertedRadius.value);
        const { rotation, coordinate } = getCommonRotateData(cursorLine.getFirstCoordinate(), cursorLine.getLastCoordinate());
        // TODO cast pro generovani stylu
        const rotationData = adjustRotateData(rotation, coordinate, resolution);
        textStyleRadius = getTextStyle({
            text: `${radius} ${convertedRadius.units}`,
            geometry: rotationData.point,
            rotation: rotationData.rotation,
            color: style.color,
            overflow: false,
        });
    }
    return textStyleRadius;
}
export function calculatePolygonMeasurements(geometry, projection, isCircle = false) {
    const totalArea = calculateArea(geometry, projection);
    let mapSegments = null;
    if (!isCircle) {
        mapSegments = geometry.getLinearRings().map((ring) => {
            const coordinates = ring.getCoordinates();
            return createSegments(coordinates, projection);
        });
    }
    return { totalArea, mapSegments };
}
export function getPolygonTextStyles(geometry, resolution, mapSegments, totalArea, style, isCircle = false) {
    let textStyles = [];
    if (!isCircle) {
        geometry.getLinearRings().forEach((ring, index) => {
            const coordinates = ring.getCoordinates();
            const segments = mapSegments[index];
            const countOfCoords = coordinates.length;
            const counter = countOfCoords < 4 ? countOfCoords - 1 : 1;
            const lastSegment = segments[segments.length - 1];
            const textStyleCircumference = getTextStyle({
                text: `${lastSegment.cumulativeSumFormat} ${lastSegment.cumulativeSumUnits}`,
                textAlign: 'center',
                geometry,
                rotation: 0,
                offsetY: 15,
                color: style.color,
                overflow: false,
            });
            textStyles = [
                ...textStyleCircumference,
                ...textStyles,
                ...getSegmentsStyles(coordinates, segments, resolution, style, counter),
            ];
        });
    }
    // create style for area
    const textStyleArea = getTextStyle({
        text: `${totalArea?.valueFormat} ${totalArea?.units}`,
        textAlign: 'center',
        geometry,
        rotation: 0,
        offsetY: isCircle ? 12 : 0,
        color: style.color,
        overflow: false,
    });
    return [...textStyles, ...textStyleArea];
}
export function getLineSumText(coordinates, resolution) {
    let first = coordinates[coordinates.length - 2];
    let second = coordinates[coordinates.length - 1];
    let textAlign = 'center';
    // check if last two points are not equal!
    if (first[0] === second[0] && first[1] === second[1]) {
        first = coordinates[coordinates.length - 3];
        second = coordinates[coordinates.length - 2];
    }
    const { rotation } = getCommonRotateData(first, second);
    const offsetedCoord = offsetCoordinate(coordinates[coordinates.length - 1], rotation - Math.PI / 2, resolution);
    const rotationData = {
        rotation,
        point: new Point(offsetedCoord),
    };
    textAlign = getTextAlign(rotationData.rotation) ?? textAlign;
    if (rotationData.rotation < 0) {
        // mirror text!
        rotationData.rotation += Math.PI;
    }
    // rotate text to right angle!
    rotationData.rotation = -(rotationData.rotation - Math.PI / 2);
    return {
        point: rotationData.point,
        rotation: rotationData.rotation,
        textAlign,
    };
}
export function getTextAlign(rotation) {
    let textAlign = undefined;
    if (rotation > 0 && rotation < Math.PI) {
        textAlign = 'left';
    }
    if (rotation < 0) {
        textAlign = 'right';
    }
    return textAlign;
}
function getHelperLineStyle(color = COLOR.ACTIVE) {
    // Need review, but it isn't used
    return [
        new Style({
            stroke: new Stroke({
                color: [255, 255, 255, 0.6],
                width: 3,
                lineDash: [8, 5],
            }),
        }),
        new Style({
            stroke: new Stroke({
                color,
                lineDash: [8, 5],
                width: 1.5,
            }),
        }),
    ];
}
//# sourceMappingURL=measure.js.map